<script setup lang="ts">
import { ButtonSeverity, TuIconButton, TuTable, TuTableRow } from "@tinyurldev/tu-ui-vue"

import useTranslation from "@Composables/useTranslation"
import PriceModel, { PriceData } from "@Modules/shared/Data/Price"
import type { CartItem } from "@Modules/shoppingcart/Data/Models/CartItemModel"

//
// Setup
const { __ } = useTranslation()

//
// Props
const props = defineProps<{
  items: CartItem[];
  loading: boolean;
  initialized: boolean;
  removingRowId?: string | null;
}>();

//
// Emit
const emit = defineEmits<{
  (e: "remove", item: CartItem): void;
}>();

//
// Methods
function formatPrice(price?: PriceData): string {
  return PriceModel(price ?? null).toHuman();
}

function isRemovable(item: CartItem): boolean {
  return !item.isRenewalService();
}

function isRemoving(item: CartItem): boolean {
  return props.removingRowId === item.rowId;
}
</script>

<template>
  <TuTable class="shopping-cart-table-price">
    <TuTableRow
      v-if="!initialized"
      class="_loading"
    >
      <td class="text-center">
        <i class="fa fa-spinner fa-spin" />
      </td>
    </TuTableRow>

    <TuTableRow v-else-if="!items.length">
      <td class="_empty text-center">
        <i class="fa-solid fa-circle-exclamation" />
        {{ __("shopping_cart.empty") }}
      </td>
    </TuTableRow>

    <template v-else>
      <TuTableRow
        v-for="item in items"
        :key="item.rowId"
        class="_cart_item"
      >
        <td>
          <div class="shopping-cart-table-price__cell-wrapper">
            <div class="shopping-cart-table-price__cell">
              <div class="shopping-cart-table-price__cell-mobile shopping-cart-table-price__cell-mobile--full">
                <p class="_item_title shopping-cart-table-price__cell-mobile-content">
                  {{ item.title }}
                </p>
              </div>
              <div class="shopping-cart-table-price__cell-mobile">
                <p class="shopping-cart-table-price__cell-mobile-title">
                  {{ __("Price") }}
                </p>
                <p class="_item_price shopping-cart-table-price__cell-mobile-content shopping-cart-table-price__price">
                  <template v-if="item.hasDiscount()">
                    <span class="_original_price shopping-cart-table-price__original-price">
                      {{ formatPrice(item.price) }}
                    </span>
                    <span class="_discounted_price shopping-cart-table-price__discounted-price">
                      {{ formatPrice(item.total) }}
                    </span>
                  </template>
                  <span v-else>
                    {{ formatPrice(item.price) }}
                  </span>
                </p>
              </div>
            </div>
            <div
              v-if="isRemovable(item)"
              class="shopping-cart-table-price__cell-mobile shopping-cart-table-price__cell-mobile--full"
            >
              <TuIconButton
                class="_remove_btn shopping-cart-table-price__action-cell-mobile"
                :aria-label="__('shopping_cart.remove_item')"
                is-outlined
                :icon-class="isRemoving(item) ? 'fa fa-spinner fa-spin' : 'fa-solid fa-trash'"
                :is-disabled="loading || isRemoving(item)"
                :severity="ButtonSeverity.ALERT"
                @click="emit('remove', item)"
              />
            </div>
          </div>
        </td>
      </TuTableRow>
    </template>
  </TuTable>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as colors;

.shopping-cart-table-price {
  td {
    font-size: typography.$s-font-size;
    line-height: typography.$s-line-height;
  }

  :deep(.tu-table) {
    display: block;
    height: 100%;
  }

  :deep(.tu-table__body) {
    max-height: 100%;
  }

  &__cell-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: spacing.$xxxs;
  }

  &__cell-mobile {
    display: grid;
    grid-template-columns: 1fr 1.5fr;
    gap: spacing.$xxxs;

    &:not(:last-child) {
      margin-bottom: spacing.$xxxs;
    }

    &--full {
      grid-template-columns: 1fr;
    }
  }

  &__cell-mobile-title {
    font-weight: typography.$font-weight-bold;
    margin: 0;
  }

  &__cell-mobile-content {
    margin: 0;
    overflow-wrap: break-word;
  }

  &__price {
    font-weight: typography.$font-weight-bold;
  }

  &__original-price {
    color: colors.$text-secondary;
    text-decoration: line-through;
    margin-right: spacing.$xxs;
  }

  &__discounted-price {
    color: colors.$secondary;
  }

  &__action-cell-mobile {
    justify-self: flex-start;
  }
}
</style>
